import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import {
  alpha,
  Box,
  IconButton,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useState } from 'react';
import {
  GetVitalsForListOfEncountersResponseData,
  GetVitalsResponseData,
} from 'utils/lib/types/api/chart-data/get-vitals.types';
import { InPersonAppointmentInformation } from 'utils/lib/types/data/appointments/appointments.types';
import { OrdersForTrackingBoardRow, OrdersForTrackingBoardTable } from 'utils/lib/types/data/orders/types';
import { TRACKING_BOARD_TABLE_WIDTH_MIN } from '../constants';
import { dataTestIds } from '../constants/data-test-ids';
import { useGetEmployees } from '../features/visits/shared/hooks/useGetEmployees';
import { AppointmentsStatusChipsCount } from './AppointmentStatusChipsCount';
import AppointmentTableHeader from './AppointmentTableHeader';
import AppointmentTableRow from './AppointmentTableRow';
import { ApptTab } from './AppointmentTabs';

const TRACKING_BOARD_TABLE_SX: SxProps<Theme> = {
  tableLayout: 'auto',
  width: '100%',
  minWidth: { xs: '100%', md: TRACKING_BOARD_TABLE_WIDTH_MIN },
};

interface AppointmentTableProps {
  appointments: InPersonAppointmentInformation[];
  tab: ApptTab;
  now: DateTime;
  updateAppointments: () => void;
  setEditingComment: (editingComment: boolean) => void;
  orders: OrdersForTrackingBoardTable;
  vitals?: GetVitalsForListOfEncountersResponseData;
}

export default function AppointmentTable({
  appointments,
  tab,
  now,
  updateAppointments,
  setEditingComment,
  orders,
  vitals,
}: AppointmentTableProps): ReactElement {
  const theme = useTheme();
  const [collapseWaiting, setCollapseWaiting] = useState<boolean>(false);
  const [collapseExam, setCollapseExam] = useState<boolean>(false);

  // Intake/provider assignment is only editable on the in-office tab, so only fetch the (rarely
  // changing) employee lists there. Discharged/Cancelled render the names read-only from the
  // appointment's participants, so they don't need this list.
  const { data: employees, isLoading: employeesLoading } = useGetEmployees({
    enabled: tab === ApptTab['in-office'],
  });
  const intakeOptions = employees?.nonProviders ?? [];
  const providerOptions = employees?.providers ?? [];

  const {
    inHouseLabOrdersByAppointmentId,
    externalLabOrdersByAppointmentId,
    nursingOrdersByAppointmentId,
    inHouseMedicationsByEncounterId,
    radiologyOrdersByAppointmentId,
    erxOrdersByEncounterId,
    proceduresByEncounterId,
    immunizationOrdersByEncounterId,
    dmeOrdersByEncounterId,
  } = orders;

  const ordersForAppointment = useCallback(
    (appointmentId: string, encounterId: string): OrdersForTrackingBoardRow => ({
      inHouseLabOrders: inHouseLabOrdersByAppointmentId[appointmentId],
      externalLabOrders: externalLabOrdersByAppointmentId[appointmentId],
      nursingOrders: nursingOrdersByAppointmentId[appointmentId],
      inHouseMedications: inHouseMedicationsByEncounterId[encounterId],
      radiologyOrders: radiologyOrdersByAppointmentId[appointmentId],
      erxOrders: erxOrdersByEncounterId[encounterId],
      procedures: proceduresByEncounterId[encounterId],
      immunizationOrders: immunizationOrdersByEncounterId[encounterId],
      dmeOrders: dmeOrdersByEncounterId[encounterId],
    }),
    [
      inHouseLabOrdersByAppointmentId,
      externalLabOrdersByAppointmentId,
      nursingOrdersByAppointmentId,
      inHouseMedicationsByEncounterId,
      radiologyOrdersByAppointmentId,
      erxOrdersByEncounterId,
      proceduresByEncounterId,
      immunizationOrdersByEncounterId,
      dmeOrdersByEncounterId,
    ]
  );

  const vitalsForAppointment = useCallback(
    (appointment: InPersonAppointmentInformation): GetVitalsResponseData | undefined => {
      return vitals?.[appointment.encounterId];
    },
    [vitals]
  );

  return (
    <>
      <AppointmentsStatusChipsCount appointments={appointments} />
      <Paper>
        <TableContainer sx={{ overflow: 'auto' }} data-testid={dataTestIds.dashboard.appointmentsTable(tab)}>
          <Table sx={TRACKING_BOARD_TABLE_SX}>
            <AppointmentTableHeader tab={tab} table="waiting-room" />
            <TableBody>
              {tab === ApptTab['in-office'] ? (
                <>
                  <TableRow>
                    <TableCell
                      sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.08), px: 1.5 }}
                      colSpan={11}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => setCollapseWaiting(!collapseWaiting)} sx={{ mr: 0.75, p: 0 }}>
                          <ArrowDropDownCircleOutlinedIcon
                            sx={{
                              color: theme.palette.primary.main,
                              rotate: collapseWaiting ? '' : '180deg',
                            }}
                          ></ArrowDropDownCircleOutlinedIcon>
                        </IconButton>
                        <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600 }}>
                          Waiting Room (
                          {
                            appointments.filter((appointmentTemp) => {
                              return appointmentTemp.status === 'arrived' || appointmentTemp.status === 'ready';
                            }).length
                          }
                          )
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {!collapseWaiting &&
                    // todo add logic to filter out appointments that are not waiting
                    appointments
                      .filter((appointmentTemp) => {
                        return appointmentTemp.status === 'arrived' || appointmentTemp.status === 'ready';
                      })
                      .map((appointment) => {
                        return (
                          <AppointmentTableRow
                            key={appointment.id}
                            appointment={appointment}
                            now={now}
                            updateAppointments={updateAppointments}
                            setEditingComment={setEditingComment}
                            tab={tab}
                            table={'waiting-room'}
                            orders={ordersForAppointment(appointment.id, appointment.encounterId)}
                            intakeOptions={intakeOptions}
                            providerOptions={providerOptions}
                            employeesLoading={employeesLoading}
                          />
                        );
                      })}
                </>
              ) : (
                appointments?.map((appointment) => {
                  return (
                    <AppointmentTableRow
                      key={appointment.id}
                      appointment={appointment}
                      now={now}
                      updateAppointments={updateAppointments}
                      setEditingComment={setEditingComment}
                      tab={tab}
                      vitals={vitalsForAppointment(appointment)}
                      orders={ordersForAppointment(appointment.id, appointment.encounterId)}
                      intakeOptions={intakeOptions}
                      providerOptions={providerOptions}
                      employeesLoading={employeesLoading}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {tab === ApptTab['in-office'] && (
        <Paper sx={{ marginTop: '16px' }}>
          <TableContainer sx={{ overflow: 'auto' }}>
            <Table sx={TRACKING_BOARD_TABLE_SX}>
              <AppointmentTableHeader tab={tab} table="in-exam" />
              <TableBody>
                <TableRow>
                  <TableCell sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.08), px: 1.5 }} colSpan={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => setCollapseExam(!collapseExam)} sx={{ mr: 0.75, p: 0 }}>
                        <ArrowDropDownCircleOutlinedIcon
                          sx={{
                            color: theme.palette.primary.main,
                            rotate: collapseExam ? '' : '180deg',
                          }}
                        ></ArrowDropDownCircleOutlinedIcon>
                      </IconButton>
                      <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600 }}>
                        Exam Rooms (
                        {
                          appointments.filter((appointmentTemp) => {
                            return appointmentTemp.status !== 'arrived' && appointmentTemp.status !== 'ready';
                          }).length
                        }
                        )
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                {!collapseExam &&
                  // todo add logic to filter out appointments that are not in exam
                  appointments
                    .filter((appointmentTemp) => {
                      return appointmentTemp.status !== 'arrived' && appointmentTemp.status !== 'ready';
                    })
                    .map((appointment) => {
                      return (
                        <AppointmentTableRow
                          key={appointment.id}
                          appointment={appointment}
                          now={now}
                          vitals={vitalsForAppointment(appointment)}
                          updateAppointments={updateAppointments}
                          setEditingComment={setEditingComment}
                          tab={tab}
                          table="in-exam"
                          orders={ordersForAppointment(appointment.id, appointment.encounterId)}
                          intakeOptions={intakeOptions}
                          providerOptions={providerOptions}
                          employeesLoading={employeesLoading}
                        />
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
}
